/**
 * Seo — reusable meta tag component powered by react-helmet-async.
 * Injects title, description, canonical, OpenGraph, Twitter Card, and optional JSON-LD.
 */
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Gefion Evolution";
const SITE_URL  = "https://www.gefion-evolution.it";
const SHOP_URL  = "https://shop.gefion-evolution.it";
const DEFAULT_IMG = `${SITE_URL}/assets/logo/gefion.png`;

export default function Seo({
  title,
  description,
  path = "",
  image = DEFAULT_IMG,
  type = "website",
  keywords = "",
  jsonLd = null,
  canonicalHost,  // optional override: "shop" | "main" | full URL
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Il posto dove le idee diventano progetti`;
  // Choose the canonical host explicitly so Shop pages canonicalize to
  // shop.gefion-evolution.it, avoiding "duplicate URL" reports in Search Console.
  let baseForCanonical;
  if (canonicalHost === "shop") baseForCanonical = SHOP_URL;
  else if (canonicalHost && canonicalHost.startsWith("http")) baseForCanonical = canonicalHost.replace(/\/$/, "");
  else if (path.startsWith("/shop")) baseForCanonical = SHOP_URL;
  else baseForCanonical = SITE_URL;
  const canonical = `${baseForCanonical}${path}`;
  const desc = description || "Gefion Evolution: taglio laser CNC, impianti elettrici, automazioni, fotovoltaico, irrigazione e trattamento acque. Officina industriale a Budoia (PN).";
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="it_IT" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/** Global LocalBusiness JSON-LD (used site-wide from App.js). */
export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}#organization`,
  name: SITE_NAME,
  legalName: "Gefion S.r.l.",
  url: SITE_URL,
  logo: DEFAULT_IMG,
  image: DEFAULT_IMG,
  description: "Officina industriale specializzata in taglio laser CNC, impianti elettrici e automazioni, irrigazione, trattamento acque e fotovoltaico.",
  telephone: "",
  email: "info@gefion-evolution.it",
  vatID: "IT01675990939",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Via 1° Maggio 6",
    addressLocality: "Budoia",
    addressRegion: "PN",
    postalCode: "33070",
    addressCountry: "IT",
  },
  geo: { "@type": "GeoCoordinates", latitude: 46.0296, longitude: 12.5457 },
  areaServed: [{ "@type": "Country", name: "Italia" }, { "@type": "AdministrativeArea", name: "Friuli-Venezia Giulia" }],
  priceRange: "€€",
  sameAs: [],
};
